import {
  Text,
  Button,
  useModalState,
  Modal,
  Primary13Regular,
  Primary13SemiBold,
  IconPlus,
  Flex
} from '@sequencehq/core-components'
import { useState } from 'react'
import { useForm, type FormFields } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import AvalaraTaxCodeInput from '../common/AvalaraTaxCodeInput'

type TaxCategoryFields = FormFields<{
  avalaraTaxCode: string
}>
type FormValues = {
  avalaraTaxCode: TaxCategoryFields['avalaraTaxCode']['value']
}

interface Props {
  taxCategoryName: string
  onAddTaxCode: (values: { avalaraTaxCode: string; taxName: string }) => void
}

const AddAvalaraTaxCodeToTaxCategoryModalUI = ({
  taxCategoryName,
  onAddTaxCode,
  onClose
}: Props & { onClose: () => void }) => {
  const { fields, queries } = useForm({
    value: {
      avalaraTaxCode: ''
    },
    fieldConfiguration: [
      {
        property: 'avalaraTaxCode',
        validation: [required]
      }
    ]
  })

  const [avalaraTaxCodeInfo, setAvalaraTaxCodeInfo] = useState<
    string | undefined
  >()

  const canSubmit = queries.isValid && avalaraTaxCodeInfo

  const handleSubmit = (formValues: FormValues) => {
    if (formValues.avalaraTaxCode && avalaraTaxCodeInfo) {
      onAddTaxCode({
        avalaraTaxCode: formValues.avalaraTaxCode,
        taxName: avalaraTaxCodeInfo
      })
    }
  }

  return (
    <>
      <Modal.Header showCloseButton withBorder onClose={onClose}>
        Add tax code
      </Modal.Header>
      <Modal.Content minHeight="auto">
        <Flex flexDirection="column" gap={3} mb="-17px">
          <Flex flexDirection="column" gap={2}>
            <Text {...Primary13Regular}>Mapping for:</Text>
            <Text as="span" {...Primary13SemiBold}>
              {taxCategoryName}
            </Text>
          </Flex>
          <AvalaraTaxCodeInput
            value={fields.avalaraTaxCode.value}
            onChange={value => {
              setAvalaraTaxCodeInfo(undefined)
              fields.avalaraTaxCode.onChange(value)
            }}
            onSuccess={setAvalaraTaxCodeInfo}
          />
        </Flex>
      </Modal.Content>
      <Modal.Footer withBorder>
        <Button
          data-testid="avalaraTaxCodeModal.cancel"
          onClick={onClose}
          style={{ marginLeft: 'auto' }}
          variant="secondary"
        >
          Cancel
        </Button>

        <Button
          data-testid="avalaraTaxCodeModal.submit"
          variant="primary"
          type="button"
          disabled={!canSubmit}
          onClick={() => {
            handleSubmit(queries.formData)
          }}
        >
          Add tax code
        </Button>
      </Modal.Footer>
    </>
  )
}

const AddAvalaraTaxCodeToTaxCategoryModal = ({ ...props }: Props) => {
  const { isModalOpen, openModal, closeModal } = useModalState()

  return (
    <>
      <Button
        data-testid="avalaraTaxCodeModal.trigger"
        variant="ghost"
        onClick={openModal}
        leadingIcon={<IconPlus color="inherit" />}
      >
        Add tax code
      </Button>

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        data-testid="avalaraTaxCodeModal"
      >
        <AddAvalaraTaxCodeToTaxCategoryModalUI
          {...props}
          onClose={closeModal}
        />
      </Modal>
    </>
  )
}

export default AddAvalaraTaxCodeToTaxCategoryModal
